import React, { useMemo, useState } from 'react';
import type { AdminLanguage, LanguageStatusAction } from '../../services/i18nService';
import {
  countRows,
  needsAttention,
  percentApproved,
  ROW_STATE_LABELS,
  summarizeSections,
  type RowState,
  type TranslationRow,
} from '../../utils/translationRows';
import type { ReviewFilter } from './SectionReview';
import { DOCUMENT_GROUP_LABELS } from '../../i18n/documents';
import { countDocumentRows, type DocumentRow } from '../../utils/documentRows';
import { isAssignedTo } from './roles';
import { useDialog, type ConfirmOptions } from '../../context/DialogContext';
import styles from '../../styles/translationAdmin.module.css';

export const LANGUAGE_STATUS_LABELS: Record<string, string> = {
  under_construction: 'Under construction',
  ready: 'Ready to publish',
  published: 'Published',
};

const FALLBACK_HELP =
  'If a string has no translation in this language, people see it in the next language in this list instead, so a page is never blank.';

const STATUS_HELP =
  'Under construction: being reviewed; only language admins can see it.\n' +
  'Ready to publish: a language admin has approved every string and marked it ready; waiting for a translation manager.\n' +
  'Published: in the language menu for everyone. It stays published when English changes later; those strings just need review again.';

const PreviewLink: React.FC<{ code: string; languageName: string }> = ({ code, languageName }) => {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/?preview=${encodeURIComponent(code)}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <>
      <div className={styles.searchRow} style={{ margin: 0 }}>
        <input className={styles.input} readOnly value={url} onFocus={(e) => e.target.select()} aria-label="Preview link" />
        <button type="button" className={styles.buttonSecondary} onClick={() => void copy()}>
          {copied ? 'Copied' : 'Copy link'}
        </button>
      </div>
      <div className={styles.muted}>
        Opens the site in {languageName} even for someone who isn&rsquo;t signed in. To see exactly what a new
        visitor sees, paste it into a private (incognito) window — a normal tab keeps you signed in. Anyone with the
        link can preview, so share it only with people helping you test.
      </div>
    </>
  );
};

const InfoTip: React.FC<{ text: string }> = ({ text }) => (
  <span className={styles.infoTip} tabIndex={0} role="img" aria-label={text}>
    ?<span className={styles.infoTipText}>{text}</span>
  </span>
);

interface LanguageOverviewProps {
  language: AdminLanguage;
  /** Display names by language code, for the fallback chain. */
  languageNames: Record<string, string>;
  rows: TranslationRow[];
  /** Null while documents are loading. */
  documentRows: DocumentRow[] | null;
  isManager: boolean;
  /** Signed-in user's email, to tell a manager whether they're also this language's admin. */
  userEmail: string;
  busy: boolean;
  onOpen: (sectionId: string, filter?: ReviewFilter, query?: string) => void;
  onOpenDocument: (docId: string) => void;
  onStatusAction: (action: LanguageStatusAction) => void;
}

function formatWhen(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const CHIP_ORDER: RowState[] = ['missing', 'stale', 'ai_draft', 'draft', 'approved'];

const LanguageOverview: React.FC<LanguageOverviewProps> = ({
  language,
  languageNames,
  rows,
  documentRows,
  isManager,
  userEmail,
  busy,
  onOpen,
  onOpenDocument,
  onStatusAction,
}) => {
  const [query, setQuery] = useState('');
  const { confirmAsync } = useDialog();
  const counts = useMemo(() => countRows(rows), [rows]);
  const sections = useMemo(() => summarizeSections(rows), [rows]);
  const stringsRemaining = needsAttention(counts);
  const docCounts = useMemo(() => (documentRows ? countDocumentRows(documentRows) : null), [documentRows]);
  const docsRemaining = docCounts ? needsAttention(docCounts) : 0;
  const remaining = stringsRemaining + docsRemaining;
  const pct = percentApproved(counts);
  const displayName = language.native_name || language.name;
  const chain = [language.code, ...(language.fallbacks || ['en'])]
    .map((code) => (code === language.code ? displayName : languageNames[code] || code))
    .join(' → ');

  const confirmThen = async (options: ConfirmOptions, action: LanguageStatusAction) => {
    if (await confirmAsync({ cancelText: 'Cancel', ...options })) onStatusAction(action);
  };

  const left = [
    stringsRemaining > 0 && `${stringsRemaining.toLocaleString()} string${stringsRemaining === 1 ? '' : 's'}`,
    docsRemaining > 0 && `${docsRemaining} document${docsRemaining === 1 ? '' : 's'}`,
  ]
    .filter(Boolean)
    .join(' and ');

  const statusDetail = (): string | null => {
    if (language.status === 'published') {
      const parts = ['Visible to everyone'];
      if (language.published_at) parts.push(`since ${formatWhen(language.published_at)}`);
      if (language.published_by) parts.push(`(published by ${language.published_by})`);
      return parts.join(' ');
    }
    if (language.status === 'ready') {
      const parts = ['Marked ready'];
      if (language.ready_by) parts.push(`by ${language.ready_by}`);
      if (language.ready_at) parts.push(`on ${formatWhen(language.ready_at)}`);
      return `${parts.join(' ')}. Not visible to members yet.`;
    }
    return 'Not visible to members yet. Language admins can preview it from the language menu.';
  };

  const renderToDo = () => {
    if (language.status === 'published') {
      return remaining > 0 ? (
        <p>
          Review and approve the {left} that are new or whose English changed. Until then, members see
          the previous translation or the AI draft.
        </p>
      ) : (
        <p>Nothing for you right now — every string and document is approved.</p>
      );
    }
    if (language.status === 'ready') {
      return (
        <>
          <p>
            {isManager
              ? `You can publish ${displayName} now. Publishing adds it to the language menu for everyone.`
              : `Nothing for you right now — waiting for a translation manager to publish ${displayName}.`}
          </p>
          {remaining > 0 && (
            <p className={styles.muted}>
              {left} {remaining === 1 ? 'is' : 'are'} no longer approved since it was marked ready
              (usually new or changed English). Reopen to finish them first.
            </p>
          )}
        </>
      );
    }
    return (
      <p>
        Review every string and document below and approve it. When everything is approved, mark{' '}
        {displayName} ready to publish.{' '}
        {isManager ? 'Then you can publish it.' : 'A translation manager then publishes it.'}
      </p>
    );
  };

  const assigned = isAssignedTo(language, userEmail, isManager);
  const roleText = isManager
    ? `Translation manager${assigned ? ` (also a language admin for ${displayName})` : ''} — you can do everything here, including publish.`
    : 'Language admin — you review, approve, and mark ready.';

  const renderActions = () => {
    if (language.status === 'published') {
      if (!isManager) return null;
      return (
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.buttonDanger}
            disabled={busy}
            onClick={() =>
              void confirmThen(
                {
                  title: `Unpublish ${displayName}?`,
                  message: 'It disappears from the language menu, and members using it switch back to English.',
                  confirmText: 'Unpublish',
                  variant: 'danger',
                },
                'unpublish'
              )
            }
          >
            Unpublish
          </button>
        </div>
      );
    }
    if (language.status === 'ready') {
      return (
        <div className={styles.actions}>
          {isManager && (
            <button
              type="button"
              className={styles.button}
              disabled={busy}
              onClick={() =>
                void confirmThen(
                  {
                    title: `Publish ${displayName}?`,
                    message: `${displayName} will appear in the language menu for everyone.`,
                    confirmText: 'Publish',
                  },
                  'publish'
                )
              }
            >
              Publish {displayName}
            </button>
          )}
          <button type="button" className={styles.buttonSecondary} disabled={busy} onClick={() => onStatusAction('reopen')}>
            Reopen for more work
          </button>
        </div>
      );
    }
    return (
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.button}
          disabled={busy || remaining > 0 || !docCounts}
          onClick={() =>
            void confirmThen(
              {
                title: `Mark ${displayName} ready to publish?`,
                message: `All ${counts.total} strings and ${docCounts?.total ?? 0} documents are approved. A translation manager can then publish it.`,
                confirmText: 'Mark ready',
              },
              'mark_ready'
            )
          }
        >
          Mark ready to publish
        </button>
        {remaining > 0 && <span className={styles.muted}>{left} left to approve</span>}
      </div>
    );
  };

  const detail = statusDetail();

  return (
    <div>
      <div className={styles.card}>
        <dl className={styles.factList}>
          <dt>Language</dt>
          <dd>
            <strong>{displayName}</strong> <span className={styles.muted}>({language.code} · {language.name})</span>
          </dd>

          <dt>
            Fallback <InfoTip text={FALLBACK_HELP} />
          </dt>
          <dd>{chain}</dd>

          <dt>
            Status <InfoTip text={STATUS_HELP} />
          </dt>
          <dd>
            <span className={styles[`badge_${language.status}`]}>
              {LANGUAGE_STATUS_LABELS[language.status] || language.status}
            </span>
            {detail && <div className={styles.muted}>{detail}</div>}
          </dd>

          {language.status !== 'published' && (
            <>
              <dt>Preview</dt>
              <dd>
                <PreviewLink code={language.code} languageName={displayName} />
              </dd>
            </>
          )}

          <dt>Your role</dt>
          <dd>{roleText}</dd>

          <dt>To do</dt>
          <dd>{renderToDo()}</dd>
        </dl>
        {renderActions()}
      </div>

      <div className={styles.card}>
        <h2>Progress</h2>
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${pct}%` }} />
        </div>
        <p className={styles.muted}>
          {counts.approved} of {counts.total} strings approved ({pct}%)
          {stringsRemaining > 0 && ` · ${stringsRemaining} need attention`}
        </p>
        {docCounts && (
          <p className={styles.muted}>
            {docCounts.approved} of {docCounts.total} documents approved
            {docsRemaining > 0 && ` · ${docsRemaining} need attention`}
          </p>
        )}
        <div className={styles.countChips}>
          {CHIP_ORDER.map((state) => (
            <button
              key={state}
              type="button"
              className={styles.countChip}
              onClick={() => onOpen('all', state)}
              title={`Show all ${ROW_STATE_LABELS[state].toLowerCase()} strings`}
            >
              <strong>{counts[state]}</strong>
              {ROW_STATE_LABELS[state]}
            </button>
          ))}
        </div>
        <form
          className={styles.searchRow}
          onSubmit={(e) => {
            e.preventDefault();
            onOpen('all', 'all', query.trim());
          }}
        >
          <input
            className={styles.input}
            type="search"
            placeholder="Find a string anywhere (English, translation, or key)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="submit" className={styles.buttonSecondary} disabled={!query.trim()}>
            Search
          </button>
        </form>
      </div>

      <div className={styles.card}>
        <h2>Sections</h2>
        <table className={styles.sectionTable}>
          <thead>
            <tr>
              <th>Section</th>
              <th>Approved</th>
              <th>Needs attention</th>
              <th>Strings</th>
            </tr>
          </thead>
          <tbody>
            {sections.map(({ section, counts: c }) => {
              const sectionPct = percentApproved(c);
              const attention = needsAttention(c);
              return (
                <tr key={section.id} className={styles.sectionRow} onClick={() => onOpen(section.id)}>
                  <td>
                    <div className={styles.sectionName}>{section.label}</div>
                    <div className={styles.sectionDesc}>{section.description}</div>
                  </td>
                  <td>
                    {sectionPct}%
                    <div className={styles.miniBar}>
                      <div className={styles.progressFill} style={{ width: `${sectionPct}%` }} />
                    </div>
                  </td>
                  <td>
                    {attention > 0 ? (
                      <span className={styles.badge_draft}>{attention}</span>
                    ) : (
                      <span className={styles.badge_approved}>Done</span>
                    )}
                  </td>
                  <td>{c.total}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className={styles.card}>
        <h2>Documents</h2>
        <p className={styles.muted}>
          Longer pages translated as a whole: privacy, about, and the step guidance. Open one to compare it with
          the English side by side.
        </p>
        {documentRows === null ? (
          <p className={styles.muted}>Loading documents…</p>
        ) : (
          <table className={styles.sectionTable}>
            <thead>
              <tr>
                <th>Document</th>
                <th>Status</th>
                <th>Last saved</th>
              </tr>
            </thead>
            <tbody>
              {documentRows.map((row, i) => {
                const groupStart = i === 0 || documentRows[i - 1].doc.group !== row.doc.group;
                return (
                  <React.Fragment key={row.doc.id}>
                    {groupStart && (
                      <tr>
                        <td colSpan={3} className={styles.docGroupLabel}>
                          {DOCUMENT_GROUP_LABELS[row.doc.group]}
                        </td>
                      </tr>
                    )}
                    <tr className={styles.sectionRow} onClick={() => onOpenDocument(row.doc.id)}>
                      <td>
                        <div className={styles.sectionName}>{row.doc.title}</div>
                      </td>
                      <td>
                        <span className={styles[`badge_${row.state}`]}>{ROW_STATE_LABELS[row.state]}</span>
                      </td>
                      <td className={styles.muted}>{formatWhen(row.saved?.updated_at)}</td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default LanguageOverview;
